import { Select } from '@televet/kibble-ui/build/components/Select';
import type { SelectOptionProps } from '@televet/kibble-ui/build/components/Select';
import { Box, Flex, HStack, Portal, useDisclosure } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import { GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useRecap } from 'shared/providers/RecapProvider/RecapProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { AiRecapEvents } from 'shared/providers/TrackingProvider/EventNames';
import {
  RecapSource,
  RecapStatus,
  useCreateOneRecapMutation,
  useFindManyAppointmentNoteTemplateQuery,
  useFindUniqueAppointmentQuery,
  useGetSignedUploadUrlMutation,
  useProcessAppointmentRecapChunksMutation,
  useStartChecksOnRecapChunksContinuityMutation,
  useUpdateOneRecapMutation,
} from 'shared/types/graphql';
import { AppointmentNoteTemplateGlobalId } from '@televet/shared-types/recap/enums/AppointmentNoteTemplateGlobalId';
import PetAvatar from '../Avatars/PetAvatar';
import CreateOrEditTemplateModal from './CreateOrEditTemplateModal';
import {
  LocalRecapStatus,
  RecapStatusDescription,
  RecapStatusToBackgroundColor,
  RecapStatusToLabel,
  RecapStatusToTypographyVariant,
} from './recap.records';
import { GraphQLError } from 'graphql';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';

enum MediaRecorderState {
  Inactive = 'inactive',
  Recording = 'recording',
  Paused = 'paused',
}

// Audio chunk duration in milliseconds (default: 60 seconds)
const AUDIO_CHUNK_DURATION_MS = 60 * 1000;

const Recap = ({ appointmentId }: RecapProps): JSX.Element => {
  const { currentClinicId, clinicUserId } = useClinicUser();

  const { isMobile } = useResolutionProvider();
  const { toggleRecap } = useRecap();
  const { trackEvent } = useTracking();
  const toast = useToast();
  const [isRecapStartingButtonLoading, setIsRecapStartingButtonLoading] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [status, setStatus] = useState(LocalRecapStatus.Ready);
  const [isTesting, setIsTesting] = useState(false);
  const [hasUserStoppedRecording, setHasUserStoppedRecording] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null); // Reference for the timer
  const pausedRef = useRef<boolean>(false); // Reference to track if recording is paused
  const [appointmentNoteTemplateId, setAppointmentNoteTemplateId] = useState<AppointmentNoteTemplateGlobalId | string>(
    AppointmentNoteTemplateGlobalId.SOAP,
  );

  const mediaStreamRef = useRef<MediaStream | null>(null); // Reference for the microphone stream
  const mediaRecorderRef = useRef<MediaRecorder | null>(null); // Reference for MediaRecorder
  const isMountedRef = useRef(true); // Track if component is mounted
  const recapIdRef = useRef<string | null>(null); // Track the current recap ID
  // Track all chunk upload promises by chunk key
  const chunkUploadPromisesRef = useRef<Map<string, Promise<void>>>(new Map());
  // Track chunk sequence number for proper ordering
  const chunkSequenceRef = useRef<number>(0);
  // Track the status before pausing to restore it correctly
  const statusBeforePauseRef = useRef<LocalRecapStatus | null>(null);

  useEffect(() => {
    if (status === LocalRecapStatus.RecordingPaused) {
      pausedRef.current = true;
    } else {
      pausedRef.current = false;
    }
  }, [status]);

  // Cleanup on unmount
  useEffect((): (() => void) => {
    return (): void => {
      isMountedRef.current = false;
      // Clear any ongoing timers
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      // Stop any ongoing recordings
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== MediaRecorderState.Inactive) {
        try {
          mediaRecorderRef.current.stop();
        } catch (error) {
          console.warn('[Recap] Error stopping recording on unmount:', error);
        }
      }
      // Stop media stream
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Safe state setter that only updates if component is still mounted
  const safeSetStatus = useCallback((newStatus: LocalRecapStatus) => {
    if (isMountedRef.current) {
      // Prevent any status change after reaching Done
      setStatus((currentStatus) => {
        if (currentStatus === LocalRecapStatus.Done) {
          console.log(`[Recap] 🚫 Ignoring status change to ${newStatus} because current status is Done`);
          return currentStatus;
        }
        console.log(`[Recap] 🔄 Status change: ${currentStatus} → ${newStatus}`);
        return newStatus;
      });
    } else {
      console.log(`[Recap] ⚠️ Attempted to set status to ${newStatus} but component is unmounted`);
    }
  }, []);

  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);

  // Queries
  const {
    data: appointmentData,
    loading: isAppointmentDataLoading,
    error: appointmentError,
  } = useFindUniqueAppointmentQuery({
    variables: {
      where: {
        id: appointmentId,
      },
    },
    fetchPolicy: GraphQLFetchPolicies.CacheFirst, // Use cache first to avoid unnecessary network requests
    errorPolicy: 'all', // Don't fail completely on network errors
  });

  const { data: appointmentNoteTemplateData, refetch: refetchNoteTemplates } = useFindManyAppointmentNoteTemplateQuery({
    variables: { where: { clinicId: { equals: currentClinicId }, isDeleted: { equals: false } } },
    skip: !currentClinicId,
  });

  // Mutations
  const [getSignedUploadUrl] = useGetSignedUploadUrlMutation();
  const [createRecap] = useCreateOneRecapMutation();
  const [updateRecap] = useUpdateOneRecapMutation();
  const [processRecapChunks] = useProcessAppointmentRecapChunksMutation();
  const [startChecksOnRecapContinuity] = useStartChecksOnRecapChunksContinuityMutation();
  // Helper function to check if there are any pending upload promises
  const hasPendingUploads = useCallback((): boolean => {
    // Since we clean up resolved promises in the finally block,
    // any promises in the map are still pending
    return chunkUploadPromisesRef.current.size > 0;
  }, []);

  // Helper function to test microphone levels
  const testMicrophoneLevel = useCallback((stream: MediaStream): Promise<boolean> => {
    return new Promise((resolve) => {
      const AudioContextClass =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const audioContext = new AudioContextClass();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      microphone.connect(analyser);
      analyser.fftSize = 256;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      let samples = 0;
      let totalLevel = 0;

      const checkLevel = (): void => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        totalLevel += average;
        samples++;
        console.log(`[Recap] [MicTest] Sample #${samples}: average level = ${average.toFixed(2)}`);

        if (samples < 10) {
          setTimeout(checkLevel, 100);
        } else {
          const avgLevel = totalLevel / samples;
          console.log(`[Recap] [MicTest] Finished. Average mic level over 10 samples: ${avgLevel.toFixed(2)}`);

          // Clean up
          microphone.disconnect();
          audioContext.close();

          // Consider microphone working if average level > 0.5
          const isWorking = avgLevel > 0.5;
          console.log(`[Recap] [MicTest] Mic working? ${isWorking}`);
          resolve(isWorking);
        }
      };

      console.log('[Recap] [MicTest] Starting microphone level test...');
      checkLevel();
    });
  }, []);

  // Helper function to upload audio chunks with infinite retry logic
  const uploadAudioChunk = useCallback(
    async (
      blob: Blob | null,
      isFinalChunk: boolean,
      recapId: string,
      chunkSequence: number,
      retryCount = 0,
    ): Promise<void> => {
      if (!blob || blob.size === 0) {
        return;
      }
      const delay = 5000; // Always retry every 5 seconds
      const chunkType = isFinalChunk ? 'final' : 'chunk';
      // const baseDelay = 5000; // 5 seconds base delay (unused)

      // Start upload attempt
      console.log(`[Recap] 🚀 Upload attempt ${retryCount + 1} for ${chunkType} chunk #${chunkSequence}`);

      try {
        // Create an abort controller for the entire upload process (GraphQL + S3)
        const controller = new AbortController();
        let hasTimedOut = false;

        const timeoutId = setTimeout(() => {
          console.log(`[Recap] ⏰ Timeout triggered for ${chunkType} chunk #${chunkSequence} after 5 seconds`);
          hasTimedOut = true;
          controller.abort();
        }, 5000); // 5 second timeout for entire process

        // Start the GraphQL request
        const requestPromise = getSignedUploadUrl({
          variables: {
            data: {
              contentType: 'audio/webm',
              key: `Appointment/Recordings/${appointmentId}-recording-${recapId}.webm.chunk-${chunkSequence
                .toString()
                .padStart(4, '0')}`,
            },
          },
          context: {
            fetchOptions: {
              signal: controller.signal,
            },
            // Disable Apollo's retry link for immediate failure detection
            skipRetry: true,
          },
        });

        // Race the request against our timeout
        const result = await Promise.race([
          requestPromise,
          new Promise<never>((_, reject) => {
            setTimeout(() => {
              if (hasTimedOut) {
                reject(new Error('Custom timeout - network request exceeded 5 seconds'));
              }
            }, 5100); // Slightly longer than abort timeout
          }),
        ]);

        // If we got here and hasTimedOut is true, Apollo overrode our abort
        if (hasTimedOut) {
          throw new Error('Network timeout - request took longer than 5 seconds');
        }

        const { data, errors } = result;

        // Check for GraphQL errors first
        if (errors && errors.length > 0) {
          console.error('[Recap] ❌ GraphQL errors from getSignedUploadUrl:', errors);
          throw new Error(`GraphQL errors: ${errors.map((e: GraphQLError) => e.message).join(', ')}`);
        }

        const uploadUrl = data?.getSignedUploadUrl.uploadUrl;

        if (!uploadUrl) {
          console.error('[Recap] ❌ Failed to get signed upload URL - no URL in response');
          console.error('[Recap] ❌ Response data:', data);
          throw new Error('Failed to get signed upload URL');
        }

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: blob,
          headers: { 'Content-Type': 'audio/webm' },
          signal: controller.signal,
        });

        // Clear timeout if request succeeds
        clearTimeout(timeoutId);

        // Validate upload response
        if (!uploadResponse.ok) {
          console.error(`[Recap] ❌ Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
          throw new Error(`Upload failed with status ${uploadResponse.status}: ${uploadResponse.statusText}`);
        }

        // Success! Handle status transition based on chunk type and current state
        console.log(`[Recap] ✅ ${chunkType} chunk #${chunkSequence} uploaded successfully`);

        if (!isFinalChunk) {
          // For intermediate chunks, restore status if we were in retry mode
          if (retryCount > 0) {
            // Only return to Recording state if user hasn't stopped recording AND MediaRecorder is still recording
            if (!hasUserStoppedRecording && mediaRecorderRef.current?.state === MediaRecorderState.Recording) {
              console.log('[Recap] 🔄 Connection recovered, returning to Recording state');
              safeSetStatus(LocalRecapStatus.Recording);
            }
            // For intermediate chunks, don't change status to Uploading unless user has actually stopped
            // The status should remain as-is (Recording, Paused, etc.) during intermediate chunk uploads
          }
        }
        // Note: Final chunk processing is now handled in ondataavailable after all uploads complete
      } catch (error) {
        console.error(`[Recap] ❌ Upload attempt ${retryCount + 1} failed for chunk #${chunkSequence}:`, error);

        // Show connection issues immediately on first failure (before any retries)
        if (retryCount === 0) {
          if (pausedRef.current === false) {
            safeSetStatus(LocalRecapStatus.UploadingWithIssues);

            // Show connection warning toast immediately for any chunk's first failure
            toast({
              title: 'Connection Issues Detected',
              description:
                "Having trouble uploading due to network issues. Please check your internet connection. We'll keep trying automatically.",
              status: 'warning',
              duration: 6000,
            });
          }
        }

        console.log(
          `[Recap] ⏳ Retrying upload for chunk #${chunkSequence} in ${delay}ms... (attempt ${retryCount + 2})`,
        );

        // Wait and then retry
        await new Promise((resolve) => setTimeout(resolve, delay));
        return uploadAudioChunk(blob, isFinalChunk, recapId, chunkSequence, retryCount + 1);
      }
    },
    [getSignedUploadUrl, appointmentId, hasUserStoppedRecording, safeSetStatus, toast],
  );

  const testMicrophone = useCallback(async (): Promise<void> => {
    setIsTesting(true);

    // Show initial instruction toast
    toast({
      title: 'Testing Microphone...',
      description: 'Please speak into your microphone now. Say a few words clearly.',
      status: 'info',
      duration: 3000,
    });

    console.log('[Recap] [MicTest] testMicrophone: Requesting microphone access...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('[Recap] [MicTest] Microphone access granted. Starting level test...');
      const isMicWorking = await testMicrophoneLevel(stream);

      // Clean up the stream
      stream.getTracks().forEach((track) => track.stop());
      console.log('[Recap] [MicTest] MediaStream tracks stopped after test.');

      if (isMicWorking) {
        // Delay success message slightly to avoid collision with instruction toast
        setTimeout(() => {
          toast({
            title: 'Microphone Test Successful',
            description: 'Your microphone is working properly!',
            status: 'success',
            duration: 3000,
          });
        }, 500);
        console.log('[Recap] [MicTest] Microphone test successful.');
      } else {
        // Delay error message to let instruction toast be read first
        setTimeout(() => {
          toast({
            title: 'Microphone Test Failed',
            description: "Your microphone appears to be silent. Make sure it's not muted and try speaking louder.",
            status: 'error',
            duration: 5000,
          });
        }, 1000);
        console.warn('[Recap] [MicTest] Microphone test failed: silent or muted.');
      }
    } catch (error) {
      console.error('[Recap] [MicTest] Microphone test failed:', error);
      // Delay error message to let instruction toast be read first
      setTimeout(() => {
        toast({
          title: 'Microphone Test Failed',
          description: 'Could not access your microphone. Please check permissions and try again.',
          status: 'error',
          duration: 5000,
        });
      }, 1000);
    } finally {
      setIsTesting(false);
      console.log('[Recap] [MicTest] testMicrophone: Test finished. isTesting set to false.');
    }
  }, [testMicrophoneLevel, toast]);

  const startRecap = useCallback(async (): Promise<void> => {
    try {
      if (isRecapStartingButtonLoading) {
        return;
      }
      setIsRecapStartingButtonLoading(true);

      // Reset state when starting a new recording (especially important after errors)
      setRecordingTime(0);
      recapIdRef.current = null;
      setHasUserStoppedRecording(false);
      chunkSequenceRef.current = 0; // Reset sequence counter
      statusBeforePauseRef.current = null; // Reset status tracking

      const recap = await createRecap({
        variables: {
          data: {
            clinic: { connect: { id: currentClinicId } },
            appointment: { connect: { id: appointmentId } },
            createdBy: { connect: { id: clinicUserId } },
            templateId: appointmentNoteTemplateId,
            status: RecapStatus.Uploading,
            source: RecapSource.Browser,
          },
        },
      });
      startChecksOnRecapContinuity({
        variables: {
          data: {
            recapId: recap.data?.createOneRecap.id || '',
          },
        },
      });

      const recapId = recap.data?.createOneRecap.id;
      if (!recapId) {
        toast({
          title: 'Error Creating Recap',
          description: 'Failed to create recap. Please try again later.',
          status: 'error',
          duration: 5000,
        });
        throw new Error('Failed to create recap');
      }

      // Store the recapId for later use
      recapIdRef.current = recapId;
      trackEvent(AiRecapEvents.Start, { appointmentId });

      // Check if mediaDevices is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Media devices not supported in this browser');
      }

      // Try to get available audio input devices first
      let devices: MediaDeviceInfo[];
      try {
        devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter((device) => device.kind === 'audioinput');
        // Audio devices detected for debugging if needed

        if (audioInputs.length === 0) {
          throw new Error('No audio input devices found');
        }
      } catch (deviceError) {
        console.warn('[Recap] Could not enumerate devices:', deviceError);
      }

      // Try different audio constraints to find a working configuration
      const audioConstraints = [
        {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000, // Match WebM/Opus standard
            channelCount: 1, // Mono for smaller file size
          },
        }, // Optimal settings
        { audio: true }, // Default
        { audio: { echoCancellation: false } }, // Disable echo cancellation
        { audio: { autoGainControl: false, noiseSuppression: false } }, // Disable processing
        { audio: { deviceId: 'default' } }, // Explicitly use default device
      ];

      let stream: MediaStream | null = null;
      let lastError = null;

      for (const constraints of audioConstraints) {
        try {
          // Audio stream constraints and settings logs removed for production
          stream = await navigator.mediaDevices.getUserMedia(constraints);

          // Test if the stream has active audio tracks
          const audioTracks = stream.getAudioTracks();
          if (audioTracks.length > 0 && audioTracks[0].readyState === 'live') {
            // Stream acquired successfully
            break;
          } else {
            // Clean up failed stream
            stream.getTracks().forEach((track) => track.stop());
            stream = null;
          }
        } catch (error) {
          console.warn('[Recap] Failed with constraints:', constraints, error);
          lastError = error;
        }
      }

      if (!stream) {
        throw lastError || new Error('Could not access microphone with any configuration');
      }

      mediaStreamRef.current = stream;

      // Initialize MediaRecorder with optimized settings for minimal memory usage
      // Check MediaRecorder support and find best mime type with robust fallback
      let mimeType = 'audio/webm;codecs=opus';
      const mediaRecorderClass = MediaRecorder as unknown as { isTypeSupported?: (type: string) => boolean };

      if (mediaRecorderClass.isTypeSupported) {
        if (!mediaRecorderClass.isTypeSupported(mimeType)) {
          console.warn('[Recap] Optimal mime type not supported, falling back');
          mimeType = 'audio/webm';
          if (!mediaRecorderClass.isTypeSupported(mimeType)) {
            mimeType = 'audio/mp4';
            if (!mediaRecorderClass.isTypeSupported(mimeType)) {
              mimeType = ''; // Let browser choose
            }
          }
        }
      } else {
        mimeType = 'audio/webm'; // Fallback for older browsers
      }

      const options: MediaRecorderOptions = {
        ...(mimeType && { mimeType }),
        audioBitsPerSecond: 64000, // 64kbps - good balance for speech
      };

      const recorder = new MediaRecorder(stream, options);

      console.log('[Recap] 🎙️ Recording started with optimal WebM configuration');

      // Handle data available (chunks)
      recorder.ondataavailable = async (event: BlobEvent): Promise<void> => {
        if (event.data.size > 0) {
          // This will be called for both intermediate chunks and the final chunk
          // Increment sequence counter for each chunk
          const chunkSequence = ++chunkSequenceRef.current;
          const isFinalChunk = recorder.state === MediaRecorderState.Inactive;

          console.log(
            `[Recap] 📦 Chunk #${chunkSequence} received: ${event.data.size} bytes (${
              isFinalChunk ? 'final' : 'intermediate'
            })`,
          );

          // Use sequence number as the key for tracking uploads
          const chunkKey = `chunk-${chunkSequence}`;

          // Start the upload and track the promise
          const uploadPromise = uploadAudioChunk(event.data, isFinalChunk, recapIdRef.current || '', chunkSequence);
          chunkUploadPromisesRef.current.set(chunkKey, uploadPromise);

          // Clean up resolved promises to keep the map accurate
          uploadPromise.finally(() => {
            chunkUploadPromisesRef.current.delete(chunkKey);
          });

          // Don't process here anymore - let stopRecap handle it
          // This ensures we process ALL chunks from the entire session, not just the final recording segment
        }
      };

      // Handle recording stop - this ensures final chunk is processed
      recorder.onstop = (): void => {
        // MediaRecorder stopped

        // Stop the timer when recording stops (not when upload starts)
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          // Recording timer logs removed for production
        }

        // Final chunk will be handled by ondataavailable
        // No additional action needed here since MediaRecorder automatically
        // fires ondataavailable with the final chunk when stop() is called
      };

      // Handle errors
      recorder.onerror = (event: Event): void => {
        console.error('[Recap] ❌ MediaRecorder error:', event);
        toast({
          title: 'Recording Error',
          description: 'An error occurred during recording. Please try again.',
          status: 'error',
          duration: 5000,
        });
      };

      // Prevent starting a new recording if already recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === MediaRecorderState.Recording) {
        console.error('[Recap] Tried to start recording but MediaRecorder is already recording.');
        toast({
          title: 'Recording Already In Progress',
          description: 'Please stop the current recording before starting a new one.',
          status: 'error',
          duration: 5000,
        });
        return;
      }

      // Start recording with time slicing (60 second chunks for production)
      console.log(`[Recap] 🎙️ Starting recording with ${AUDIO_CHUNK_DURATION_MS / 1000}-second chunks`);

      // Set status to Recording BEFORE starting the recorder to ensure it's available when first chunk is generated
      safeSetStatus(LocalRecapStatus.Recording);

      recorder.start(AUDIO_CHUNK_DURATION_MS);
      // This will be called for both intermediate chunks (every 60 seconds in production)
      mediaRecorderRef.current = recorder;

      // Start a timer that updates every second
      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);

      // Test microphone levels after recording has started and user has had time to speak
      setTimeout(async () => {
        try {
          if (stream) {
            const isMicWorking = await testMicrophoneLevel(stream);
            if (!isMicWorking) {
              console.warn('[Recap] Microphone appears to be silent');
              toast({
                title: 'Microphone Warning',
                description:
                  'Your microphone appears to be silent. Please check your microphone settings and ensure it is not muted.',
                status: 'warning',
                duration: 5000,
              });
            }
          }
        } catch (testError) {
          console.warn('[Recap] Could not test microphone levels:', testError);
        }
      }, 2000);
    } catch (error) {
      console.error('[Recap] Error accessing microphone or starting recording:', error);

      let errorMessage = 'Unable to access microphone. ';

      if (error instanceof DOMException) {
        switch (error.name) {
          case 'NotFoundError':
            errorMessage += 'No microphone found. Please check that a microphone is connected and try again.';
            break;
          case 'NotAllowedError':
            errorMessage += 'Microphone access denied. Please allow microphone permissions and try again.';
            break;
          case 'NotReadableError':
            errorMessage +=
              'Microphone is being used by another application. Please close other applications using the microphone and try again.';
            break;
          case 'OverconstrainedError':
            errorMessage += 'Microphone settings are not supported. Please try with a different microphone.';
            break;
          case 'AbortError':
            errorMessage += 'Microphone access was aborted. Please try again.';
            break;
          case 'SecurityError':
            errorMessage += 'Microphone access blocked for security reasons. Please check your browser settings.';
            break;
          default:
            errorMessage += `Error: ${error.message}`;
        }
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }

      toast({
        title: 'Microphone Error',
        description: errorMessage,
        status: 'error',
        duration: 8000,
      });
    } finally {
      // Reset recap starting state
      setIsRecapStartingButtonLoading(false);
    }
  }, [
    isRecapStartingButtonLoading,
    createRecap,
    currentClinicId,
    appointmentId,
    clinicUserId,
    appointmentNoteTemplateId,
    startChecksOnRecapContinuity,
    trackEvent,
    safeSetStatus,
    toast,
    uploadAudioChunk,
    testMicrophoneLevel,
  ]);

  const stopRecap = useCallback(async (): Promise<void> => {
    try {
      setHasUserStoppedRecording(true); // Mark that user explicitly stopped recording

      updateRecap({
        variables: {
          where: { id: recapIdRef.current || '' },
          data: { status: RecapStatus.Transcribing, durationSeconds: recordingTime },
        },
      });

      // Note: Timer will be cleared by recorder.onstop event handler
      // to ensure it's stopped exactly when recording ends

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== MediaRecorderState.Inactive) {
        const recorder = mediaRecorderRef.current;

        // Stop the recorder - this will trigger ondataavailable for the final chunk
        // and the onstop handler will clear the timer
        recorder.stop();

        // Clean up the recorder reference
        mediaRecorderRef.current = null;
      }

      // Stop all tracks of the media stream
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }

      // Set local status to processing while uploading final chunks
      safeSetStatus(LocalRecapStatus.Processing);

      // Wait for ALL chunks from the entire session to finish uploading
      try {
        console.log('[Recap] ⏳ Waiting for all chunks to finish uploading before processing...');
        await Promise.all(Array.from(chunkUploadPromisesRef.current.values()));

        console.log('[Recap] ✅ All chunks uploaded, now processing recap...');
        // Now process all chunks together
        await processRecapChunks({ variables: { data: { recapId: recapIdRef.current || '' } } });
        safeSetStatus(LocalRecapStatus.Done);
      } catch (error) {
        console.error('[Recap] ❌ Failed to upload all chunks or process recap:', error);
        safeSetStatus(LocalRecapStatus.Error);
        toast({
          title: 'Upload Failed',
          description: 'Failed to complete upload. Please try recording again.',
          status: 'error',
          duration: 8000,
        });
      } finally {
        chunkUploadPromisesRef.current.clear();
      }
    } catch (error) {
      console.error('[Recap] Error stopping recording:', error);
    }
  }, [safeSetStatus, recordingTime, updateRecap, processRecapChunks, toast]);

  const pauseRecap = useCallback((): void => {
    // Store the current status before pausing
    statusBeforePauseRef.current = status;
    safeSetStatus(LocalRecapStatus.RecordingPaused);

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === MediaRecorderState.Recording) {
      mediaRecorderRef.current.pause();

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [safeSetStatus, status]);

  const resumeRecap = useCallback((): void => {
    // When resuming, check what status we had before pausing
    const previousStatus = statusBeforePauseRef.current;

    if (previousStatus === LocalRecapStatus.UploadingWithIssues && hasPendingUploads()) {
      // We were having upload issues before pausing and still have pending uploads
      safeSetStatus(LocalRecapStatus.UploadingWithIssues);
    } else {
      // Return to Recording status (normal case)
      safeSetStatus(LocalRecapStatus.Recording);
    }

    // Clear the stored status
    statusBeforePauseRef.current = null;

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === MediaRecorderState.Paused) {
      mediaRecorderRef.current.resume();

      // Resume the timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    }
  }, [safeSetStatus, hasPendingUploads]);

  const clearAndClose = useCallback((): void => {
    console.log('[Recap] 🚪 clearAndClose called, current status:', status);
    trackEvent(AiRecapEvents.Done, { appointmentId });

    console.log('[Recap] 🚪 Cleaning up and resetting all state');
    // Reset all state when closing
    setRecordingTime(0);
    recapIdRef.current = null;
    setHasUserStoppedRecording(false);
    safeSetStatus(LocalRecapStatus.Ready);
    toggleRecap();
  }, [appointmentId, trackEvent, toggleRecap, safeSetStatus, status]);

  const POPOVER_DEFAULT = { width: 360, height: 'auto', x: window.innerWidth / 2 - 380 / 2, y: 40 };

  const { isOpen: isAppointmentTemplateModalOpen, onToggle: toggleAppointmentTemplateModal } = useDisclosure();

  const handleEditTemplateClick = useCallback(
    (templateId: AppointmentNoteTemplateGlobalId | string): void => {
      setAppointmentNoteTemplateId(templateId);
      toggleAppointmentTemplateModal();
    },
    [toggleAppointmentTemplateModal],
  );

  const handleAppointmentTemplateSelect = useCallback((templateId: AppointmentNoteTemplateGlobalId | string): void => {
    setAppointmentNoteTemplateId(templateId);
  }, []);

  const handleCreateNewTemplateClick = useCallback((): void => {
    setAppointmentNoteTemplateId(AppointmentNoteTemplateGlobalId.NEW);
    toggleAppointmentTemplateModal();
  }, [toggleAppointmentTemplateModal]);

  const selectOptions = useMemo(() => {
    const options: SelectOptionProps[] = [
      {
        label: 'Soap Notes',
        children: (
          <TemplateSelectOption
            templateName="Soap Notes"
            templateId={AppointmentNoteTemplateGlobalId.SOAP}
            selectedTemplateId={appointmentNoteTemplateId}
          />
        ),
        onSelect: (): void => handleAppointmentTemplateSelect(AppointmentNoteTemplateGlobalId.SOAP),
        value: AppointmentNoteTemplateGlobalId.SOAP,
      },
      {
        label: 'Appointment Summary',
        children: (
          <TemplateSelectOption
            templateName="Appointment Summary"
            templateId={AppointmentNoteTemplateGlobalId.SUMMARY}
            selectedTemplateId={appointmentNoteTemplateId}
          />
        ),
        onSelect: (): void => handleAppointmentTemplateSelect(AppointmentNoteTemplateGlobalId.SUMMARY),
        value: AppointmentNoteTemplateGlobalId.SUMMARY,
      },
    ];

    const templates: SelectOptionProps[] =
      appointmentNoteTemplateData?.findManyAppointmentNoteTemplate.map((template) => {
        return {
          label: template.name,
          children: (
            <TemplateSelectOption
              templateName={template.name}
              templateId={template.id}
              handleEditClick={(): void => handleEditTemplateClick(template.id)}
              selectedTemplateId={appointmentNoteTemplateId}
            />
          ),
          onSelect: (): void => handleAppointmentTemplateSelect(template.id),
          value: template.id,
        };
      }) ?? [];

    options.push(...templates, {
      children: (
        <Flex justifyContent="flex-end">
          <Button size="sm" variant="ghostNeutral" width="100%" onClick={handleCreateNewTemplateClick}>
            Create new template
          </Button>
        </Flex>
      ),
    });
    return options;
  }, [
    appointmentNoteTemplateData?.findManyAppointmentNoteTemplate,
    appointmentNoteTemplateId,
    handleAppointmentTemplateSelect,
    handleCreateNewTemplateClick,
    handleEditTemplateClick,
  ]);

  // Debug: Log current status on each render
  // Main render debug log (removed for production)

  return (
    <Portal>
      <CreateOrEditTemplateModal
        templateId={appointmentNoteTemplateId}
        isOpen={isAppointmentTemplateModalOpen}
        onClose={toggleAppointmentTemplateModal}
        refetchTemplates={refetchNoteTemplates}
        setTemplateId={setAppointmentNoteTemplateId}
      />
      <Box position="absolute" top={0} left={0} className="recap_modal">
        <Rnd
          enableResizing={false}
          enableUserSelectHack={true}
          default={POPOVER_DEFAULT}
          bounds="body"
          dragHandleClassName="drag-handle"
          style={{
            background: 'var(--chakra-colors-background-default)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 'var(--chakra-zIndices-modal)',
          }}
        >
          {/* Header */}
          <Box className="drag-handle recap_modal_header">
            <Flex
              bg={RecapStatusToBackgroundColor[status]}
              p={1}
              minH="40px"
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              position="relative"
              cursor="move"
            >
              <Flex align="center" justify="flex-start" pl={2}>
                <Icon name="grid" size="sm" variant={RecapStatusToTypographyVariant[status]} />
              </Flex>
              <HStack spacing={1} align="center" justify="center">
                {status === LocalRecapStatus.Recording && <Icon name="record" variant="danger" size="md" />}
                {status === LocalRecapStatus.RecordingPaused && <Icon name="record" size="md" />}
                {(status === LocalRecapStatus.Uploading ||
                  status === LocalRecapStatus.Processing ||
                  status === LocalRecapStatus.UploadingWithIssues) && <Spinner size="sm" />}
                <Text variant={RecapStatusToTypographyVariant[status]}>{RecapStatusToLabel[status]}</Text>
              </HStack>
              <Flex justify="flex-end">
                {(status === LocalRecapStatus.Ready ||
                  status === LocalRecapStatus.Done ||
                  status === LocalRecapStatus.Error) && (
                  <Button
                    iconName="close"
                    className="cancelDrag"
                    variant="ghost"
                    size="sm"
                    p={0}
                    paddingInline={0}
                    paddingInlineStart={0}
                    borderRadius="lg"
                    onTouchStart={isMobile ? toggleRecap : undefined}
                    onClick={toggleRecap}
                  />
                )}
              </Flex>
            </Flex>
          </Box>

          {/* Content */}

          <Flex gap={2} p={4} direction="column">
            <Text size="md" fontWeight="bold">
              Otto AI Recap
            </Text>
            <Text size="xs">{RecapStatusDescription[status]}</Text>

            {/* Recording Card */}
            {isAppointmentDataLoading ? (
              <Flex h="94px" alignItems="center" justifyContent="center">
                <Spinner />
              </Flex>
            ) : appointmentError ? (
              <Flex
                bg="background.subtle"
                p="3"
                justifyContent="center"
                alignItems="center"
                borderRadius="base"
                border="solid 1px #e8e9e9"
                h="94px"
              >
                <Text size="sm" color="text.subtle">
                  Unable to load appointment details
                </Text>
              </Flex>
            ) : (
              appointmentData?.findUniqueAppointment && (
                <Flex
                  bg="background.subtle"
                  p="3"
                  justifyContent="space-between"
                  borderRadius="base"
                  border="solid 1px #e8e9e9"
                >
                  <Flex direction="column" gap={1}>
                    <Text as="p" size="sm">
                      {formatTime(recordingTime)}
                    </Text>
                    <Text as="p" size="sm">
                      {appointmentData.findUniqueAppointment.appointmentType?.name ?? ''} -{' '}
                      {format(parseISO(appointmentData.findUniqueAppointment.startAt), 'h:mm a')}
                    </Text>
                    <Flex alignItems="center" gap={2}>
                      <PetAvatar
                        isDeceased={!!appointmentData.findUniqueAppointment.clinicPet?.isDeceased}
                        speciesType={appointmentData.findUniqueAppointment.clinicPet?.speciesType}
                        size="sm"
                        iconOnly={true}
                      />
                      <Text size="sm" fontWeight="semibold">
                        {appointmentData.findUniqueAppointment.clinicPet?.name}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              )
            )}
            <Select
              label="Select a template"
              value={appointmentNoteTemplateId}
              options={selectOptions}
              isDisabled={status !== LocalRecapStatus.Ready}
              selectedOptionContainerProps={{ color: 'text.active' }}
            />

            <Flex justifyContent="flex-end" gap={2}>
              {status === LocalRecapStatus.Ready && (
                <>
                  <Button
                    isDisabled={isAppointmentDataLoading || !!appointmentError}
                    variant="secondarySubtle"
                    size="sm"
                    isLoading={isTesting}
                    onClick={testMicrophone}
                  >
                    Test Mic
                  </Button>
                  <Button
                    isDisabled={isAppointmentDataLoading || !!appointmentError}
                    leftIcon={<Icon size="sm" name="record" />}
                    variant="primary"
                    size="sm"
                    onClick={startRecap}
                    isLoading={isRecapStartingButtonLoading}
                  >
                    Start
                  </Button>
                </>
              )}

              {status === LocalRecapStatus.Error && (
                <>
                  <Button variant="secondary" size="sm" onClick={clearAndClose}>
                    Cancel
                  </Button>
                  <Button
                    leftIcon={<Icon size="sm" name="record" />}
                    variant="primary"
                    size="sm"
                    onClick={startRecap}
                    isLoading={isRecapStartingButtonLoading}
                  >
                    Try Again
                  </Button>
                </>
              )}

              {(status === LocalRecapStatus.Recording ||
                (status === LocalRecapStatus.UploadingWithIssues && !hasUserStoppedRecording)) && (
                <Button
                  leftIcon={<Icon size="sm" name="pause" />}
                  variant="tertiary"
                  size="sm"
                  onClick={(): void => {
                    trackEvent(AiRecapEvents.Pause, { appointmentId });
                    pauseRecap();
                  }}
                >
                  Pause
                </Button>
              )}

              {status === LocalRecapStatus.RecordingPaused && (
                <Button
                  leftIcon={<Icon size="sm" name="play" />}
                  variant="secondary"
                  size="sm"
                  onClick={(): void => {
                    trackEvent(AiRecapEvents.Resume, { appointmentId });
                    resumeRecap();
                  }}
                >
                  Resume
                </Button>
              )}

              {(status === LocalRecapStatus.Recording ||
                status === LocalRecapStatus.RecordingPaused ||
                (status === LocalRecapStatus.UploadingWithIssues && !hasUserStoppedRecording)) && (
                <Button
                  leftIcon={<Icon size="sm" name="stopRecording" />}
                  variant="primaryDestructive"
                  size="sm"
                  onClick={stopRecap}
                >
                  Stop
                </Button>
              )}
              {(status === LocalRecapStatus.Uploading ||
                status === LocalRecapStatus.Processing ||
                status === LocalRecapStatus.Done) && (
                <Button
                  isDisabled={status === LocalRecapStatus.Uploading || status === LocalRecapStatus.Processing}
                  variant="tertiary"
                  size="sm"
                  onClick={clearAndClose}
                >
                  Done
                </Button>
              )}
            </Flex>
          </Flex>
        </Rnd>
      </Box>
    </Portal>
  );
};

const TemplateSelectOption = ({
  templateName,
  templateId,
  handleEditClick,
  selectedTemplateId,
}: {
  templateName: string;
  templateId: string;
  handleEditClick?: (templateId: string) => void;
  selectedTemplateId: string | AppointmentNoteTemplateGlobalId;
}): JSX.Element => {
  const isActive = useMemo(() => {
    return selectedTemplateId === templateId;
  }, [selectedTemplateId, templateId]);
  return (
    <Flex justifyContent="space-between" alignItems="center" minH="32px">
      <Text size="sm" color={isActive ? 'text.accent' : 'text.default'}>
        {templateName}
      </Text>
      {handleEditClick && (
        <Button
          onClick={(): void => {
            if (handleEditClick && !!templateId) {
              handleEditClick(templateId);
            }
          }}
          size="sm"
          variant="ghost"
          iconName="pen"
        />
      )}
    </Flex>
  );
};

// Ensure RecapProps is defined
interface RecapProps {
  appointmentId: string;
}

// Ensure formatTime is defined
const formatTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export default Recap;
